import { useInView } from '../../hooks/useInView'
import { ACCENT, CARD, CARD_SHADOW, BentoShell, GridPattern, DotPattern } from '../bento'

function fadeUp(inView: boolean, delay: number) {
  return {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : 'translateY(24px)',
    transition: `opacity 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms, transform 0.65s cubic-bezier(0.45,0,0.15,1) ${delay}ms`,
  }
}

// Согласованный к публикации срез результатов рекламодателей.
// Публикуем только тип бизнеса, географию и порядок новых клиентов в месяц.
// Не добавлять выручку, ставки и конверсии: это коммерческая информация,
// публикация требует отдельного согласия контрагента.
interface CaseData {
  company: string
  /** Монохромный логотип с альфа-каналом (как в Partners): цвет даёт mask-image, файл красить не нужно.
   *  Без него в шапке карточки остаётся название текстом. */
  logo?: { src: string; width: number; height: number }
  product: string
  industry: string
  text: string
  clients: string // новых клиентов в месяц
  /** Эмодзи в плашке над числом — по сфере кейса. */
  icon: string
}

const CASES: CaseData[] = [
  {
    company: 'Федеральная сеть стоматологий',
    product: 'Стоматологические клиники',
    industry: 'Медицина',
    text: 'Сеть представлена в 20 городах России и привлекает новых пациентов через витрину RIS.Promo.',
    clients: '4 000–5 000',
    icon: '🧑‍⚕️',
  },
  {
    company: 'Puzzle English',
    product: 'Онлайн-школа английского',
    industry: 'Образование',
    text: 'Школа английского языка для взрослых и детей, работает по всей России.',
    clients: '≈ 6 000',
    icon: '🧑‍🏫',
  },
  {
    company: 'Абрикосик',
    product: 'Сеть салонов эпиляции',
    industry: 'Красота',
    text: 'Салоны эпиляции из Москвы, записывают новых клиентов через витрину RIS.Promo.',
    clients: '1 500–2 000',
    icon: '💆',
  },
]

/** Одна строка бенто: инфо-карточка (3/5) + крупное число новых клиентов (2/5). */
function CaseRow({ c }: { c: CaseData }) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:gap-4 lg:grid-cols-5">

      {/* Инфо-карточка: название, тип бизнеса, описание с географией, категория */}
      <div
        className={`${CARD} relative flex flex-col overflow-hidden lg:col-span-3`}
        style={CARD_SHADOW}
      >
        <GridPattern />
        <div className="relative flex grow flex-col p-4 sm:p-6">
          {c.logo ? (
            <div
              role="img"
              aria-label={c.company}
              className="mb-1"
              style={{
                width: c.logo.width,
                height: c.logo.height,
                backgroundColor: '#27272a',
                WebkitMaskImage: `url("${c.logo.src}")`,
                maskImage: `url("${c.logo.src}")`,
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'left center',
                maskPosition: 'left center',
              }}
            />
          ) : (
            <p className="text-[19px] font-medium leading-6 tracking-[-0.2px] text-[#27272a] sm:text-[24px] sm:leading-8">
              {c.company}
            </p>
          )}
          <p className="font-body mt-0.5 text-[13px] leading-5 text-[#676b94] sm:text-[15px]">{c.product}</p>
          <p className="font-body mt-3 max-w-[440px] text-[14px] leading-5 text-[#52525b] sm:mt-4 sm:text-[16px] sm:leading-6">
            {c.text}
          </p>
        </div>
        <div className="relative flex items-center justify-between border-t border-[#eef0f3] px-4 py-3 sm:px-6 sm:py-4">
          <span className="font-body inline-flex items-center gap-2 rounded-full bg-[#f4f5f7] px-2.5 py-1 text-[12px] leading-5 text-[#394959] sm:px-3 sm:py-1.5 sm:text-[13px]">
            <span className="h-1.5 w-1.5 rounded-full sm:h-2 sm:w-2" style={{ background: ACCENT }} aria-hidden />
            {c.industry}
          </span>
        </div>
      </div>

      {/* Крупное число: новых клиентов в месяц */}
      <div
        className={`${CARD} relative flex flex-col overflow-hidden p-4 sm:p-6 lg:col-span-2`}
        style={CARD_SHADOW}
      >
        <DotPattern />
        {/* Эмодзи вместо рисованной svg-иконки (на остальных секциях — Why,
            CtaFinal, карточки уведомлений в Stats — значки тоже эмодзи).
            Плашка светло-сиреневая, как у категорий в карточках уведомлений
            (#eef2ff): на синей заливке прежней иконки эмодзи теряется.
            aria-hidden: смысл несёт подпись под числом, дублировать нечего. */}
        <span
          aria-hidden
          className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-[#eef2ff] text-[20px] leading-none sm:h-[52px] sm:w-[52px] sm:rounded-[16px] sm:text-[26px]"
        >
          {c.icon}
        </span>
        <p className="font-display relative mt-6 whitespace-nowrap text-[38px] font-bold leading-none tracking-[-1.5px] text-[#041527] sm:mt-auto sm:text-[52px] lg:text-[56px] lg:tracking-[-2.5px]">
          {c.clients}
        </p>
        <p className="font-body relative mt-2 text-[14px] leading-5 text-[#52525b] sm:mt-3 sm:text-[16px] sm:leading-6">
          новых клиентов каждый месяц
        </p>
      </div>

    </div>
  )
}

export function Cases() {
  const { ref, inView } = useInView(0.15, '0px 0px -80px 0px')

  return (
    <section
      id="cases"
      className="mx-auto flex max-w-[1920px] flex-col items-center gap-6 px-4 py-14 sm:gap-8 sm:px-6 sm:py-20 lg:px-[7%] lg:py-28"
    >
      <h2
        ref={ref}
        className="font-display max-w-[748px] text-center text-[28px] max-[359px]:text-[24px] font-semibold leading-[1.14] text-[#041527] sm:text-[36px] lg:text-[44px]"
        style={fadeUp(inView, 0)}
      >
        Результаты рекламодателей
      </h2>

      <div className="w-full max-w-[1200px]" style={fadeUp(inView, 100)}>
        <BentoShell>
          <div className="flex flex-col gap-2 sm:gap-4">
            {CASES.map((c) => (
              <CaseRow key={c.company} c={c} />
            ))}
          </div>
        </BentoShell>
      </div>

    </section>
  )
}
